import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NumbersIcon from '@mui/icons-material/Numbers';
import PersonIcon from '@mui/icons-material/Person';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Fab, MenuItem, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    CustomerUserDetails,
    EventDetails,
    EventOverlayRequest,
    ExtendedAccident,
    PolicyV2,
} from '../../../backendsdk';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { TrackedIconButton as IconButton } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useCustomer from '../../../hooks/customer';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { formatLicensePlateWithInternalId } from '../../../utils/Str';
import palette from '../../ColorPalette';
import CreateOverlayDialog from '../Event/CreateOverlayDialog';
import EventShareMenuComponent from '../Event/EventShareMenu';
import { OE_ROLES } from '../Users/UsersComponent';
import ChangeStageMenu from './AccidentV1/Modal/ChangeStageMenu';
import SendAccidentModal from './AccidentV1/Modal/SendAccidentModal';

interface AccidentModalHeaderProps {
    details: ExtendedAccident;
    eventDetails?: EventDetails;
    onClose: CallableFunction;
    checkDuplicate: CallableFunction;
    onSave: CallableFunction;
    sending: boolean;
    openTagging?: CallableFunction;
    disableSaving: boolean;
    policies?: PolicyV2[];
    loadingPolicies?: boolean;
    showStageButton?: boolean;
    showShareButton?: boolean;
    changeStage?: (stage: string) => Promise<void> | undefined;
    setAlert: CallableFunction;
    hideSave?: boolean;
}

const AccidentModalHeader: React.FC<AccidentModalHeaderProps> = (props: AccidentModalHeaderProps) => {
    const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
    const [anchorElChangeStage, setAnchorElChangeStage] = useState<null | HTMLElement>(null);
    const [anchorElShare, setAnchorElShare] = useState<null | HTMLElement>(null);
    const [showCreateEventOverlay, setShowCreateEventOverlay] = useState<boolean>(false);
    const [isSendAccidentOpen, setIsSendAccidentOpen] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const { t } = useTranslation();
    const { profile } = useProfile();
    const { api, agencyApi } = useApi();
    const isMobile = useIsMobile();
    const { customerUsers } = useCustomer();
    const { license_plate, driver_name, timestamp, stage, sub_fleet_name, location, accident_id } = props.details;
    const licensePlateDisplay = formatLicensePlateWithInternalId(
        license_plate,
        props.eventDetails?.device?.internal_id,
        profile.customer.settings.show_vehicle_internal_id ?? false,
    );
    const formatted_stage = t(
        `content.accidents.stage.${profile.customer.settings.country}.${stage.toLocaleLowerCase()}`,
        stage,
    );

    const isChangeStageOpen = Boolean(anchorElChangeStage);
    const handleChangeStageMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElChangeStage((prev) => (!prev ? event.currentTarget : null));
    };
    const handleChangeStageMenuClose = () => {
        setAnchorElChangeStage(null);
    };

    const handleShareMenuClose = () => {
        setAnchorElShare(null);
    };

    const onSave = () => {
        if (props.details.accident_id === 0) {
            props.checkDuplicate();
        } else {
            props.onSave();
        }
    };

    const fields = [
        { value: licensePlateDisplay, icon: <LocalShippingIcon />, title: t('content.accidents.form.license_plate') },
        { value: driver_name, icon: <PersonIcon />, title: t('content.accidents.form.driver_name') },
        {
            value: dayjs.unix(timestamp).format(profile.dateTimeFormat),
            icon: <CalendarMonthIcon />,
            title: t('content.accidents.form.date'),
        },
        { value: formatted_stage, icon: <ViewColumnIcon />, title: t('content.accidents.stage.stage') },
        { value: sub_fleet_name, icon: <GroupsIcon />, title: t('content.accidents.form.sub_fleet') },
        { value: location, icon: <LocationOnIcon />, title: t('content.accidents.form.location') },
        { value: accident_id, icon: <NumbersIcon />, title: t('content.accidents.id') },
    ];

    const isLicensePlateValid =
        props.policies !== undefined && props.policies.length > 0
            ? props.policies.filter(
                  (p) => p.license_plate.replaceAll('-', '') == props.details.license_plate.replaceAll('-', ''),
              ).length > 0
            : true;

    const saveButton = (
        <Box>
            <IconButton
                id="save-accident-btn"
                disabled={props.sending || props.disableSaving || props.loadingPolicies}
                size="small"
                sx={{ color: palette.black }}
                onClick={() => {
                    if (isLicensePlateValid) {
                        onSave();
                    } else {
                        setConfirmDialog(true);
                    }
                }}
            >
                <SaveIcon />
            </IconButton>
        </Box>
    );

    const getBucketName = () => {
        const videoUrl = props.eventDetails
            ? Object.entries(props.eventDetails.artifact_path).find((e) => e[0].endsWith('mp4'))?.[1]
            : '';
        if (videoUrl) {
            const parsedUrl = new URL(videoUrl);
            return parsedUrl.hostname.split('.')[0];
        }
        return '';
    };

    let saveButtonTooltip;
    if (props.disableSaving) {
        saveButtonTooltip = t('content.accidents.form.finish_tagging');
    } else if (props.details.accident_id === 0) {
        saveButtonTooltip = t('content.accidents.form.create');
    } else {
        saveButtonTooltip = t('content.accidents.form.update');
    }

    const handleSendAccident = (recipients: CustomerUserDetails[], message: string) => {
        setSending(true);
        agencyApi
            .agencyV1AccidentAccidentIdSendPost({
                accidentId: props.details.accident_id,
                sendAccidentRequest: {
                    recipients: recipients.map((u) => u.user_id),
                    message,
                },
            })
            .then(() => {
                setIsSendAccidentOpen(false);
                props.setAlert({
                    message: t('content.accidents.modal_header.share.send_accident_success'),
                    type: 'success',
                    duration: 6000,
                });
            })
            .catch(() => {
                props.setAlert({
                    message: t('content.accidents.modal_header.share.send_accident_error'),
                    type: 'error',
                    duration: 6000,
                });
            })
            .finally(() => {
                setSending(false);
            });
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'secondary.main',
                    position: 'sticky',
                    top: 0,
                    p: 0.5,
                    zIndex: 1500,
                }}
            >
                {showCreateEventOverlay && OE_ROLES.includes(profile.role) && props.eventDetails ? (
                    <CreateOverlayDialog
                        details={props.eventDetails}
                        onClose={() => setShowCreateEventOverlay(false)}
                        onSuccess={(eventOverlayRequest: EventOverlayRequest) => {
                            const bucketName = getBucketName();
                            if (bucketName) {
                                eventOverlayRequest.cache_bucket = bucketName;
                            }
                            api.apiAdminOverlayPost({ eventOverlayRequest })
                                .then(() => {
                                    props.setAlert({
                                        message: t('content.events.create_overlay.success'),
                                        type: 'success',
                                        duration: 6000,
                                    });
                                    setShowCreateEventOverlay(false);
                                })
                                .catch(() => {
                                    props.setAlert({
                                        message: t('content.events.create_overlay.failed'),
                                        type: 'error',
                                        duration: 6000,
                                    });
                                });
                        }}
                    />
                ) : null}
                <ConfirmDialog
                    isDialogOpen={confirmDialog}
                    isLoading={props.sending}
                    headerText={'content.accidents.form.invalid_vin.header'}
                    bodyText={['content.accidents.form.invalid_vin.body1', 'content.accidents.form.invalid_vin.body2']}
                    buttonText={'content.accidents.form.invalid_vin.save'}
                    buttonColor="secondary"
                    onConfirm={() => {
                        onSave();
                        setConfirmDialog(false);
                    }}
                    onClose={() => setConfirmDialog(false)}
                />
                <ChangeStageMenu
                    open={isChangeStageOpen}
                    currentStage={stage}
                    anchorElChangeStage={anchorElChangeStage}
                    handleChangeStageMenuClose={handleChangeStageMenuClose}
                    changeStage={(stage: string) => (props.changeStage ? props.changeStage(stage) : null)}
                />
                <EventShareMenuComponent
                    details={props.eventDetails}
                    anchorElShare={anchorElShare}
                    setAnchorElShare={setAnchorElShare}
                    setAlert={props.setAlert}
                    showCoaching={true}
                >
                    <MenuItem
                        id="copy-link-menu-item"
                        onClick={() => {
                            const fullUrl = window.location.toString();
                            navigator.clipboard.writeText(fullUrl);
                            props.setAlert({
                                message: t('content.accidents.modal_header.share.share_link_copied'),
                                type: 'info',
                                duration: 6000,
                            });
                            handleShareMenuClose();
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{t('content.accidents.modal_header.share.copy_link')}</Typography>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        id="send-accident-menu-item"
                        onClick={() => {
                            setIsSendAccidentOpen(true);
                            handleShareMenuClose();
                        }}
                    >
                        {t('content.accidents.modal_header.share.send_accident')}
                    </MenuItem>
                </EventShareMenuComponent>
                {!isMobile ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 1,
                            width: '100%',
                            alignItems: 'center',
                        }}
                    >
                        {fields.map((field) => {
                            return (
                                <Box key={field.title}>
                                    <Tooltip title={field.title}>
                                        <Box sx={{ display: 'flex', px: 1 }}>
                                            {field.icon}
                                            <Typography
                                                sx={{
                                                    ml: 0.5,
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {field.value}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Typography sx={{ ml: 1 }}>{`${t('content.accidents.accident')} ${accident_id}`}</Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                    {OE_ROLES.includes(profile.role) && props.openTagging ? (
                        <Tooltip
                            title={
                                props.details.accident_id === 0
                                    ? t('content.accidents.tags.disabled_on_new')
                                    : t('content.accidents.tags.tooltip')
                            }
                        >
                            {/* div is used to show tooltip on disabled button */}
                            <Box>
                                <IconButton
                                    id="tag-accident-btn"
                                    size="small"
                                    sx={{ color: palette.black }}
                                    onClick={() => (props.openTagging ? props.openTagging() : null)}
                                    disabled={props.details.accident_id === 0}
                                >
                                    <LocalOfferIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {props.showShareButton ? (
                        <Tooltip title={t('content.accidents.modal_header.share.header')}>
                            <Box>
                                <IconButton
                                    id="btn-share"
                                    size="small"
                                    sx={{ color: palette.black }}
                                    onClick={(e) => setAnchorElShare(e.currentTarget)}
                                    disabled={props.details.accident_id === 0}
                                >
                                    <ShareIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {OE_ROLES.includes(profile.role) && !isMobile ? (
                        <Tooltip title={t('content.events.create_overlay.header')}>
                            <Box>
                                <IconButton
                                    id="btn-create-overlay"
                                    size="small"
                                    sx={{ color: palette.black }}
                                    onClick={() => setShowCreateEventOverlay((p: boolean) => !p)}
                                    disabled={props.details.accident_id === 0 || !props.eventDetails}
                                >
                                    <PhotoFilterIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {props.showStageButton ? (
                        <Tooltip title={t('content.accidents.modal_header.change_stage')}>
                            <Box>
                                <IconButton
                                    id="btn-change-stage"
                                    size="small"
                                    sx={{ color: palette.black }}
                                    onClick={handleChangeStageMenuClick}
                                    disabled={props.details.accident_id === 0}
                                >
                                    <FormatLineSpacingIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {!isMobile ? (
                        <Tooltip title={saveButtonTooltip}>
                            <Box>{saveButton}</Box>
                        </Tooltip>
                    ) : null}
                    <IconButton id="btn-close-modal" size="small" onClick={() => props.onClose()}>
                        <CloseIcon sx={{ color: isMobile ? palette.black : undefined }} />
                    </IconButton>
                </Box>
                {isSendAccidentOpen ? (
                    <SendAccidentModal
                        onSend={handleSendAccident}
                        onClose={() => setIsSendAccidentOpen(false)}
                        users={customerUsers}
                        sending={sending}
                    />
                ) : null}
            </Box>
            {isMobile && !props.hideSave ? (
                <Fab
                    id="save-accident-btn"
                    color="secondary"
                    aria-label="save"
                    disabled={props.sending || props.disableSaving || props.loadingPolicies}
                    onClick={() => {
                        if (isLicensePlateValid) {
                            onSave();
                        } else {
                            setConfirmDialog(true);
                        }
                    }}
                    sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1500 }}
                >
                    <SaveIcon />
                </Fab>
            ) : null}
        </>
    );
};

export default AccidentModalHeader;
